import { useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";

interface PathParams {
  lltsBranchId?: string;
}

/**
 * Custom hook that provides navigation utilities with automatic lltsBranchId parameter preservation.
 * This hook ensures that the lltsBranchId parameter is consistently maintained across all navigation actions.
 */
export function useNavigation() {
  const history = useHistory();
  const { lltsBranchId } = useParams<PathParams>();

  /**
   * Builds a path with the lltsBranchId parameter preserved.
   * @param path - The target path (without lltsBranchId)
   * @returns The complete path with lltsBranchId preserved
   */
  const buildPath = useCallback(
    (path: string): string => {
      // Remove leading slash if present to normalize the path
      const normalizedPath = path.startsWith("/") ? path.slice(1) : path;

      // If we have an lltsBranchId, prepend it to the path
      if (lltsBranchId) {
        return `/${lltsBranchId}/${normalizedPath}`;
      }

      // If no lltsBranchId, return the path as-is (with leading slash)
      return `/${normalizedPath}`;
    },
    [lltsBranchId]
  );

  /**
   * Navigates to a path while preserving the lltsBranchId parameter.
   * @param path - The target path (without lltsBranchId)
   * @param replace - Whether to replace the current history entry instead of pushing a new one
   */
  const navigateTo = useCallback(
    (path: string, replace = false): void => {
      const fullPath = buildPath(path);
      if (replace) {
        history.replace(fullPath);
      } else {
        history.push(fullPath);
      }
    },
    [buildPath, history]
  );

  /**
   * Navigates to a path with additional parameters while preserving the lltsBranchId parameter.
   * @param path - The target path (without lltsBranchId)
   * @param params - Additional path parameters to append
   * @param replace - Whether to replace the current history entry instead of pushing a new one
   */
  const navigateToWithParams = useCallback(
    (path: string, params: string[], replace = false): void => {
      const pathWithParams = `${path}/${params.join("/")}`;
      navigateTo(pathWithParams, replace);
    },
    [navigateTo]
  );

  /**
   * Goes back in history.
   */
  const goBack = useCallback((): void => {
    history.goBack();
  }, [history]);

  /**
   * Goes forward in history.
   */
  const goForward = useCallback((): void => {
    history.goForward();
  }, [history]);

  /**
   * Replaces the current history entry with a new path while preserving lltsBranchId.
   * @param path - The target path (without lltsBranchId)
   */
  const replaceTo = useCallback(
    (path: string): void => {
      navigateTo(path, true);
    },
    [navigateTo]
  );

  return {
    /**
     * The current lltsBranchId parameter from the URL
     */
    lltsBranchId,

    /**
     * Builds a complete path with lltsBranchId preserved
     */
    buildPath,

    /**
     * Navigates to a path while preserving lltsBranchId
     */
    navigateTo,

    /**
     * Navigates to a path with additional parameters while preserving lltsBranchId
     */
    navigateToWithParams,

    /**
     * Replaces the current history entry while preserving lltsBranchId
     */
    replaceTo,

    /**
     * Goes back in history
     */
    goBack,

    /**
     * Goes forward in history
     */
    goForward,

    /**
     * Direct access to the history object for advanced use cases
     */
    history
  };
}
